// Agent Plugins 1.0.0, skills component. Validation is local; never fetch a schema.
const yaml = require('js-yaml');
const SCHEMA = 'https://agent-plugins.org/schemas/1.0.0/plugin.schema.json';
const MAX_PACKAGE_CHARS = 48000;
const object = value => value !== null && typeof value === 'object' && !Array.isArray(value);
const pluginName = name => typeof name === 'string' && name === name.trim() && name.length <= 64 && /^(?!.*(?:--|\.\.))[a-z0-9](?:[a-z0-9.-]*[a-z0-9])?$/.test(name);
const skillName = name => typeof name === 'string' && name === name.trim() && [...name].length <= 64 && /^[\p{Ll}\p{Lo}\p{N}]+(?:-[\p{Ll}\p{Lo}\p{N}]+)*$/u.test(name);
function validateManifest(input) {
  if (!object(input)) throw new Error('plugin.json must contain an object.');
  if (input.$schema !== SCHEMA) throw new Error('Unsupported plugin format. Use Agent Plugins 1.0.0 with a root plugin.json.');
  if (!pluginName(input.name)) throw new Error('Invalid plugin name in plugin.json.');
  const warnings = [], manifest = { $schema: SCHEMA, name: input.name };
  const strings = ['version', 'description', 'homepage', 'repository', 'license'];
  for (const key of strings) if (key in input) {
    if (typeof input[key] !== 'string') throw new Error(`plugin.json: ${key} must be a string.`);
    manifest[key] = input[key];
  }
  if ('author' in input) {
    if (!object(input.author) || Object.entries(input.author).some(([key, value]) => !['name', 'email', 'url'].includes(key) || typeof value !== 'string'))
      throw new Error('plugin.json: invalid author.');
    manifest.author = { ...input.author };
  }
  if ('keywords' in input) {
    if (!Array.isArray(input.keywords) || input.keywords.some(item => typeof item !== 'string')) throw new Error('plugin.json: keywords must be strings.');
    manifest.keywords = [...input.keywords];
  }
  // Runtime requirements describe compatibility, never authority or credentials.
  const gab = input.extensions?.['ai.gab'];
  if (gab !== undefined) {
    if (!object(gab) || !['work', 'mac-browser'].includes(gab.runtime)) throw new Error('Unsupported Gab AI plugin runtime.');
    manifest.extensions = { 'ai.gab': { runtime: gab.runtime } };
  }
  if ('extensions' in input && !object(input.extensions)) warnings.push('Ignored non-object extensions.');
  for (const key of Object.keys(input)) if (!['$schema', 'name', ...strings, 'author', 'keywords', 'extensions'].includes(key))
    warnings.push(`Ignored unknown manifest field: ${key}.`);
  return { manifest, warnings };
}
function validateSkill(directory, content) {
  if (!skillName(directory) || typeof content !== 'string' || content.length > 32000) throw new Error('Invalid or oversized skill.');
  const match = content.replace(/^\uFEFF/, '').match(/^---\r?\n([\s\S]*?)\r?\n---(?:\r?\n|$)/);
  if (!match) throw new Error('SKILL.md needs YAML frontmatter.');
  const meta = yaml.load(match[1], { schema: yaml.JSON_SCHEMA });
  if (!object(meta) || meta.name !== directory || !skillName(meta.name)) throw new Error('The skill name must match its directory.');
  if (typeof meta.description !== 'string' || !meta.description.trim() || [...meta.description].length > 1024) throw new Error('Invalid skill description.');
  if ('compatibility' in meta && (typeof meta.compatibility !== 'string' || !meta.compatibility.length || [...meta.compatibility].length > 500)) throw new Error('Invalid skill compatibility.');
  for (const key of ['license', 'allowed-tools']) if (key in meta && typeof meta[key] !== 'string') throw new Error(`Invalid skill ${key}.`);
  if ('metadata' in meta && (!object(meta.metadata) || Object.values(meta.metadata).some(value => typeof value !== 'string'))) throw new Error('Invalid skill metadata.');
  const allowed = ['name', 'description', 'compatibility', 'license', 'metadata', 'allowed-tools'];
  if (Object.keys(meta).some(key => !allowed.includes(key))) throw new Error('Unknown skill frontmatter field.');
  return { directory, name: meta.name, description: meta.description, content,
    ...(meta.compatibility && { compatibility: meta.compatibility }) };
}
function loadPluginPackage(input) {
  if (!object(input) || JSON.stringify(input).length > MAX_PACKAGE_CHARS) throw new Error('Plugin exceeds the 48 KB instruction limit.');
  const { manifest, warnings } = validateManifest(input.manifest);
  const skills = [], names = new Set();
  if (Array.isArray(input.warnings)) warnings.push(...input.warnings.filter(item => typeof item === 'string').slice(0, 32));
  const mcpServers = {};
  if (input.mcpServers !== undefined) {
    if (!object(input.mcpServers) || Object.keys(input.mcpServers).length > 8) throw new Error('Invalid MCP server list (maximum 8).');
    for (const [name, spec] of Object.entries(input.mcpServers)) {
      if (!pluginName(name) || !object(spec)) throw new Error('Invalid MCP server definition.');
      // Packages carry public endpoints only. Never import tokens, environment
      // variables, command execution, or custom credential-bearing headers.
      if (Object.keys(spec).some(key => !['type', 'url'].includes(key)) || (spec.type && spec.type !== 'http'))
        throw new Error('MCP plugins support HTTPS URLs only; commands, headers, and secrets cannot be imported.');
      let url;
      try { url = new URL(spec.url); } catch { throw new Error('Invalid MCP service URL.'); }
      if (url.protocol !== 'https:' || url.username || url.password || url.search || url.hash || url.href.length > 2048)
        throw new Error('MCP service URLs must use HTTPS without credentials or query parameters.');
      Object.defineProperty(mcpServers, name, { value: { type: 'http', url: url.href }, enumerable: true });
    }
  } else if (input.hasMcp) warnings.push('MCP configuration was not imported. Update the Mac app and import this folder again.');
  if (!Array.isArray(input.skills)) throw new Error('Invalid plugin skill list.');
  if (input.skills.length > 32) throw new Error('A plugin can contain up to 32 skills.');
  for (const candidate of input.skills) {
    try {
      const skill = validateSkill(candidate?.directory, candidate?.content);
      if (names.has(skill.name)) throw new Error('Duplicate skill name.');
      names.add(skill.name); skills.push(skill);
    } catch (error) { warnings.push(`Skipped ${String(candidate?.directory || 'skill').slice(0, 64)}: ${error.message}`); }
  }
  return { manifest, skills, warnings: [...new Set(warnings)], hasMcp: input.hasMcp === true || Object.keys(mcpServers).length > 0,
    ...(Object.keys(mcpServers).length && { mcpServers }) };
}
function pluginRuntime(pkg) {
  // Retain the requirements of packages installed by the original Mac client.
  if (pkg.manifest.extensions?.['ai.gab']?.runtime === 'mac-browser' ||
      pkg.skills.some(skill => /requires[^\n]*(?:\bMac\b|macOS)|\bmac_desktop\b/i.test(skill.compatibility || skill.content))) return 'mac-browser';
  return 'work';
}
module.exports = { SCHEMA, MAX_PACKAGE_CHARS, validateManifest, validateSkill, loadPluginPackage, pluginName, pluginRuntime };
